/**
 * parseDateFromText
 * Extracts a natural-language date/time from free-form text using chrono-node.
 * Designed for inline NLP parsing in task quick-add inputs.
 * 
 * Pure utility — no React, no platform code. Works in browser and React Native.
 */

import * as chrono from 'chrono-node';

// ============================================================================
// Types
// ============================================================================

export interface ParsedDateResult {
  /** Character index where the date expression starts in the original text */
  index: number;
  /** The raw text that was matched (e.g., "tomorrow at 3pm") */
  text: string;
  /** Parsed date in YYYY-MM-DD format */
  date: string;
  /** Parsed time in HH:mm format, or undefined if only a date was detected */
  time?: string;
}

export interface ExcludeRange {
  start: number;
  end: number;
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTime(d: Date): string {
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Check if a range overlaps with any of the excluded ranges.
 */
function overlapsExcluded(
  matchStart: number,
  matchEnd: number,
  excludeRanges: ExcludeRange[]
): boolean {
  return excludeRanges.some(
    (range) => matchStart < range.end && matchEnd > range.start
  );
}

/**
 * Check if the match is followed by characters that indicate it's part of a word
 * (e.g., "Tomorrow's" or "MayDay"). This prevents false positives.
 */
function isPartOfWord(text: string, matchEnd: number): boolean {
  if (matchEnd >= text.length) return false;
  const nextChar = text[matchEnd];
  // Followed by apostrophe+s (possessive) or a letter (compound word)
  if (nextChar === "'" && matchEnd + 1 < text.length && /[a-zA-Z]/.test(text[matchEnd + 1])) {
    return true;
  }
  if (/[a-zA-Z]/.test(nextChar)) {
    return true;
  }
  return false;
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Parse a natural-language date/time expression from free-form text.
 * 
 * @param text - The full input text to search for date expressions
 * @param referenceDate - The reference date for relative expressions (e.g., "tomorrow").
 *                        Should come from campaign timezone (getCampaignToday()).
 * @param excludeRanges - Character ranges to skip (e.g., @mention ranges to avoid
 *                        parsing "May" from "@May Johnson")
 * @returns The first valid date match, or null if no date was found
 * 
 * @example
 * parseDateFromText("Call voters tomorrow at 3pm", new Date())
 * // { index: 13, text: "tomorrow at 3pm", date: "2026-02-15", time: "15:00" }
 * 
 * @example
 * parseDateFromText("Follow up Friday", new Date())
 * // { index: 10, text: "Friday", date: "2026-02-20", time: undefined }
 * 
 * @example
 * parseDateFromText("@May Johnson call", new Date(), [{ start: 0, end: 13 }])
 * // null — "May" is inside an excluded range
 */
export function parseDateFromText(
  text: string,
  referenceDate: Date,
  excludeRanges: ExcludeRange[] = []
): ParsedDateResult | null {
  if (!text.trim()) return null;

  const results = chrono.casual.parse(text, {
    instant: referenceDate,
    timezone: undefined, // Use referenceDate's timezone context
  }, {
    forwardDate: true,
  });

  for (const result of results) {
    const matchStart = result.index;
    const matchEnd = matchStart + result.text.length;

    // Skip matches that overlap with excluded ranges (@mentions, #categories)
    if (overlapsExcluded(matchStart, matchEnd, excludeRanges)) {
      continue;
    }

    // Skip matches that are part of a larger word (e.g., "Tomorrow's Heroes")
    if (isPartOfWord(text, matchEnd)) {
      continue;
    }

    const parsedDate = result.start.date();
    const dateStr = formatDate(parsedDate);

    // Determine if time was explicitly mentioned
    let timeStr: string | undefined;
    if (result.start.isCertain('hour')) {
      timeStr = formatTime(parsedDate);
    }

    return {
      index: matchStart,
      text: result.text,
      date: dateStr,
      time: timeStr,
    };
  }

  return null;
}
